import { useI18n } from 'next-localization';
import classNames from 'classnames';
import BaseSkeleton from 'components/Generic/BaseSkeleton';
import CustomScroll from 'components/Functional/CustomScroll';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { useMemo, useState } from 'react';
import useHelpers from 'hooks/helpers';
import { standardFormat } from '@/constants/dateFormats';
import { PremiumDiscountTable } from 'model/ApiGateway';

type Props = {
  data: PremiumDiscountTable;
  isFetching: boolean;
  className: string;
};

type TableDataWithPossibleValues = {
  thead: {
    key: string;
    name: string;
  }[];
  tbody: {
    key: string;
    value: string;
  }[];
};

const FundDetailsPremiumDiscountBarChartCurrentDataTable = ({
  className,
  isFetching,
  data,
}: Props) => {
  const { t } = useI18n();
  const deviceType: string = useWindowDimensions();
  const [gradientPosition, setGradientPosition] = useState('gradient-right');
  const { localizeDate, formatNumber, formatCurrency } = useHelpers();

  const tableDataWithPossibleValues: TableDataWithPossibleValues = useMemo(() => {
    if (!data) return;

    return {
      thead: [
        {
          key: 'date',
          name: t('fundDetailsPremiumDiscountDates'),
        },
        {
          key: 'currency',
          name: t('fundDetailsPremiumDiscountCurrency'),
        },
        {
          key: 'nav',
          name: t('fundDetailsPremiumDiscountNav'),
        },
        {
          key: 'midpoint',
          name: t('fundDetailsPremiumDiscountMidpoint'),
        },
        {
          key: 'premiumDiscount',
          name: t('fundDetailsPremiumDiscount'),
        },
      ],
      tbody: [
        {
          key: 'date',
          value: localizeDate(data.date, standardFormat),
        },
        {
          key: 'currency',
          value: data.currency,
        },
        {
          key: 'nav',
          value: data.nav ? formatCurrency(data.nav, data.currency, 'compact') : '',
        },
        {
          key: 'midpoint',
          value: data.midpoint ? formatNumber(data.midpoint) : '',
        },
        {
          key: 'premiumDiscount',
          value: data.premiumDiscount ? `${formatNumber(data.premiumDiscount)}%` : '',
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const tableData = useMemo(() => {
    if (!data) return;
    return {
      thead: tableDataWithPossibleValues.thead.filter((item) => data[item.key]),
      tbody: tableDataWithPossibleValues.tbody.filter((item) => data[item.key]),
    };
  }, [tableDataWithPossibleValues, data]);
  return (
    <div className={className}>
      {isFetching ? (
        <BaseSkeleton height="120px" />
      ) : tableData ? (
        <>
          <div className="position-relative">
            <div
              id="currentDataWrapper"
              className={classNames('abrdn-table', {
                scrollable: deviceType === 'mobile',
                [gradientPosition]: gradientPosition,
              })}
            >
              <table
                id="currentDataTable"
                className="not-striped"
                tabIndex="0"
                aria-label={t('fundDetailsPremiumDiscountCurrentTitle')}
              >
                <thead>
                  <tr>
                    {tableData.thead.map((th) => (
                      <th key={th.key}>{th.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {tableData.tbody.map((td, index) => {
                      if (index === 0) {
                        return (
                          <th key={index} scope="row">
                            {td.value || t('notAvailableShortMessage')}
                          </th>
                        );
                      } else {
                        return <td key={td.key}>{td.value || t('notAvailableShortMessage')}</td>;
                      }
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {deviceType === 'mobile' && (
            <CustomScroll
              wrapperId="currentDataWrapper"
              tableId="currentDataTable"
              changeGradientPosition={setGradientPosition}
            />
          )}
        </>
      ) : (
        <div className="body-default">{t('noResultsMessage')}</div>
      )}
    </div>
  );
};

export default FundDetailsPremiumDiscountBarChartCurrentDataTable;
